import Router from 'next/router';
import { memo, useCallback, useRef } from 'react';

import { type AirActionTrackingLocation, useTrackSavedFilterCreated } from '@air/analytics';
import { type AssetSearchFiltersCalculated } from '@air/api/types';
import { useToasts } from '@air/provider-toast';

import { type ModalFormData, SavedFilterModal } from '~/components/SavedFilter/components/SavedFilterModal';
import { useCreateSavedFilter } from '~/components/SavedFilter/shared/hooks/useCreateSavedFilter';
import { DEFAULT_CRITERIA } from '~/components/SavedFilter/shared/utils/defaultSavedFilter';
import { createSavedSearchRoute } from '~/constants/routes';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useGetCustomFieldColors } from '~/swr-hooks/customFields/useGetCustomFieldColors';

export type CreateSavedFiltersModalProps = {
  trackLocation: AirActionTrackingLocation;
  filters?: AssetSearchFiltersCalculated;
} & AirModalProps;

const MODAL_DESCRIPTION =
  'Saved searches auto-update as new content is added to the workspace. All workspace members can see them, but only Admins can create or edit them. Try out a saved filter to find content or stay organized!';

const MODAL_LABEL = 'Create saved search';

export const CreateSavedFilterModal = memo(({ onClose, filters, trackLocation }: CreateSavedFiltersModalProps) => {
  const { createSavedFilter } = useCreateSavedFilter();
  const { currentWorkspace } = useCurrentWorkspace();
  const { search } = useSearchParams();
  const { trackSavedFilterCreated } = useTrackSavedFilterCreated();
  const { showToast } = useToasts();
  const { data: customFieldColorListResp } = useGetCustomFieldColors();
  // NOTE: The type "CustomFieldColor" is the same as the "SavedFilterColor" type
  const colors = customFieldColorListResp?.data || [];
  const defaultColor = colors.find((c) => c.name === 'Grey') || colors[0];
  // Colors arrive behind a two-hop chain (workspace -> permissions -> colors), so the CTA must
  // stay disabled until then; otherwise the click is discarded with no feedback. isPending
  // updates on the next render, so the ref blocks a second click in the same turn. The modal
  // then disables the button and Enter from isSubmitting.
  const isSubmitReady = !!currentWorkspace?.id && !!defaultColor;
  const submitLockRef = useRef<boolean>();

  const handleApplyClick = useCallback(
    (modalFormData: ModalFormData) => {
      if (submitLockRef.current || !currentWorkspace?.id || !defaultColor) {
        return;
      }

      submitLockRef.current = true;

      createSavedFilter.mutate(
        {
          ...modalFormData,
          color: defaultColor,
          criteria: {
            ...DEFAULT_CRITERIA,
            filters,
            search: search ?? '',
            workspaceId: currentWorkspace.id,
          },
        },
        {
          onSuccess: (createdSavedFilter) => {
            trackSavedFilterCreated({ trackLocation });
            onClose();
            Router.push(createSavedSearchRoute(createdSavedFilter.id));
          },
          onError: () => {
            showToast('Failed to publish saved search', { color: 'red' });
          },
          onSettled: () => {
            submitLockRef.current = false;
          },
        },
      );
    },
    [
      createSavedFilter,
      currentWorkspace?.id,
      defaultColor,
      filters,
      onClose,
      search,
      showToast,
      trackLocation,
      trackSavedFilterCreated,
    ],
  );

  return (
    <SavedFilterModal
      modalDescription={MODAL_DESCRIPTION}
      modalLabel={MODAL_LABEL}
      isSubmitReady={isSubmitReady}
      isSubmitting={createSavedFilter.isPending}
      onClose={onClose}
      onSubmit={handleApplyClick}
      primaryCTACopy="Publish to workspace"
    />
  );
});

CreateSavedFilterModal.displayName = 'CreateSavedFilterModal';
